import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, MapPin, Clock, User as UserIcon, MessageSquare } from "lucide-react";

const POLL_MS = 30000;

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - then);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function rankLabel(rank) {
  if (rank === 1) return { text: "Perfect match", classes: "bg-emerald-50 text-emerald-700 border-emerald-200" };
  if (rank <= 3) return { text: "Great fit", classes: "bg-blue-50 text-blue-900 border-blue-200" };
  return { text: "Good match", classes: "bg-slate-100 text-slate-700 border-slate-200" };
}

function urgencyClasses(u) {
  if (u === "ASAP") return "bg-red-50 text-red-700 border-red-200";
  if (u === "Within a Week") return "bg-amber-50 text-amber-700 border-amber-200";
  return "bg-slate-100 text-slate-700 border-slate-200";
}

export default function MatchedJobsFeed() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get("/me/matched-jobs");
        if (!cancelled) setItems(r.data || []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    const t = setInterval(load, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  if (loading) {
    return (
      <div className="mt-4 text-sm text-slate-500" data-testid="matched-loading">
        Finding your best-fit jobs…
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="mt-4" data-testid="matched-empty">
        <div className="p-6 rounded-lg border border-slate-200 bg-slate-50 text-center">
          <Sparkles className="w-5 h-5 text-orange-600 mx-auto mb-2" />
          <div className="font-medium text-slate-900">No matches yet</div>
          <div className="text-sm text-slate-600 mt-1">
            Add your city, trade categories and hourly rate on your profile so we can match you with the right homeowners.
          </div>
          <Link to="/profile/edit">
            <Button variant="outline" className="mt-3 border-blue-900 text-blue-900">
              Complete profile
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-3" data-testid="matched-feed">
      {items.map((m) => {
        const label = rankLabel(m.rank);
        return (
          <div
            key={m.match_id}
            data-testid={`matched-card-${m.job_id}`}
            className="p-4 rounded-lg border border-slate-200 bg-white hover:border-blue-300 hover:shadow-sm transition-all"
          >
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className="bg-blue-900 text-white border-blue-900 font-medium">
                  #{m.rank} Match
                </Badge>
                <Badge variant="outline" className={`${label.classes} border font-normal`}>
                  {label.text}
                </Badge>
                {m.urgency && (
                  <Badge variant="outline" className={`${urgencyClasses(m.urgency)} border font-normal`}>
                    {m.urgency}
                  </Badge>
                )}
                <span className="text-xs text-slate-500 inline-flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {timeAgo(m.created_at)}
                </span>
              </div>
            </div>

            <div className="mt-3">
              <div className="font-semibold text-slate-900 leading-snug">{m.title}</div>
              <div className="text-sm text-slate-600 mt-1 line-clamp-2">{m.description}</div>
            </div>

            <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3 text-xs text-slate-600">
              <div className="inline-flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-slate-400" />
                {m.city}, {m.province}
              </div>
              <div className="inline-flex items-center gap-1.5">
                <UserIcon className="w-3.5 h-3.5 text-slate-400" />
                {m.homeowner?.name || "Homeowner"}
              </div>
              <div className="inline-flex items-center gap-1.5">
                <MessageSquare className="w-3.5 h-3.5 text-slate-400" />
                {m.quote_count} quote{m.quote_count === 1 ? "" : "s"} so far
              </div>
            </div>

            {m.match_reason && (
              <div className="mt-3 text-xs text-slate-500 italic border-l-2 border-orange-200 pl-3">
                {m.match_reason}
              </div>
            )}

            <div className="mt-4 flex gap-2 flex-wrap">
              <Button
                data-testid={`matched-view-${m.job_id}`}
                variant="outline"
                className="border-slate-300"
                onClick={() => nav(`/jobs/${m.job_id}`)}
              >
                View details
              </Button>
              <Button
                data-testid={`matched-quote-${m.job_id}`}
                className="bg-blue-900 hover:bg-blue-950 text-white"
                onClick={() => nav(`/jobs/${m.job_id}?action=quote`)}
                disabled={m.already_quoted}
              >
                {m.already_quoted ? "Quote sent" : "Send quote"}
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
